import { ApiProject } from "@incendium/api";
import { Box, Stack, Typography } from "@mui/material";
import { CenterPage } from "Components/CenterPage/CenterPage";
import { TabButton } from "Components/UI/TabButton";
import { TabContainer } from "Components/UI/TabContainer";
import { AllProjectsSelector } from "features/project";
import { enumToArray } from "Helpers/enumToText";
import { useSelectedProject } from "Hooks";
import PaymentPage from "Pages/Account/PaymentPage";
import UsersPage from "Pages/Account/UsersPage";
import AICreditsHistoryPage from "Pages/Account/AICreditsHistoryPage";
import { useCallback, useMemo, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { useUpdateEffect } from "react-use";
import GeneralSettingsPage from "Pages/GeneralSettingsPage";

enum Tab {
  general = "General",
  ai = "AI",
  payment = "Billing",
  users = "Users",
}

function AccountPage() {
  const history = useHistory();
  const location = useLocation();
  const { selectedProject, lastSelectedProject } = useSelectedProject();
  const [projectSelection, setProjectSelection] = useState<ApiProject | null>(
    null,
  );

  useUpdateEffect(() => {
    if (!projectSelection) {
      return;
    }
    history.push(
      `/clients/${projectSelection.clientId}/projects/${projectSelection.id}/account/general`,
    );
  }, [projectSelection]);

  const project = useMemo(() => {
    return selectedProject || lastSelectedProject;
  }, [selectedProject, lastSelectedProject]);

  const onTabChange = useCallback(
    (tab: Tab) => {
      const baseURL = `/clients/${selectedProject?.clientId}/projects/${selectedProject?.id}/account/`;
      switch (tab) {
        case Tab.general:
          history.push(baseURL + "general");
          break;
        case Tab.ai:
          history.push(baseURL + "ai");
          break;
        case Tab.payment:
          history.push(baseURL + "payment");
          break;
        case Tab.users:
          history.push(baseURL + "users");
          break;
        default:
          break;
      }
    },
    [selectedProject, history],
  );

  const isActive = useCallback(
    (tab: Tab) => {
      switch (tab) {
        case Tab.general:
          return location.pathname.includes("/account/general");
        case Tab.ai:
          return location.pathname.includes("/account/ai");
        case Tab.payment:
          return location.pathname.includes("payment");
        case Tab.users:
          return location.pathname.includes("users");

        default:
          return false;
      }
    },
    [location],
  );

  return (
    <Stack>
      {!project ? (
        <CenterPage calcHeight>
          <Typography variant="subtitle1" mb={2}>
            Which Project would you like to manage?
          </Typography>

          <AllProjectsSelector
            project={projectSelection}
            setProject={setProjectSelection}
            variant="outlined"
            size="medium"
            sx={{ width: 300 }}
          />
        </CenterPage>
      ) : (
        <>
          <TabContainer fullWidth>
            {enumToArray(Tab).map((t) => (
              <TabButton
                key={t}
                onClick={() => onTabChange(t)}
                isActive={isActive(t)}
              >
                {t}
              </TabButton>
            ))}
          </TabContainer>
          <Box mt={4}>
            <Switch>
              <Route
                exact
                path="/clients/:clientId/projects/:projectId/account/general"
              >
                <GeneralSettingsPage />
              </Route>
              <Route
                exact
                path="/clients/:clientId/projects/:projectId/account/ai"
              >
                <AICreditsHistoryPage />
              </Route>
              <Route
                exact
                path="/clients/:clientId/projects/:projectId/account/payment"
              >
                <PaymentPage />
              </Route>
              <Route path="/clients/:clientId/projects/:projectId/account/users">
                <UsersPage />
              </Route>

              <Route exact path="/account">
                <Redirect
                  to={`/clients/${project.clientId}/projects/${project.id}/account/general`}
                />
              </Route>
            </Switch>
          </Box>
        </>
      )}
    </Stack>
  );
}

export default AccountPage;
